import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import API_URL from '../config/api';
import './MessageInbox.css';
import GiftClaimButton from './gifts/GiftClaimButton';
import { getBackendErrorMessage } from '../utils/toastError';
import useEscapeKey from '../hooks/useEscapeKey';

const CATEGORY_META = {
  all: { label: 'Todos', icon: '📬' },
  system: { label: 'Sistema', icon: '🛠️' },
  transport: { label: 'Transporte', icon: '🚚' },
  commerce: { label: 'Tiendas', icon: '🏪' },
  games: { label: 'Juegos', icon: '🎮' }
};

const MessageInbox = ({ disablePrefetch = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  useEscapeKey(() => setIsOpen(false), isOpen);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const lastRefreshRef = useRef(0);
  const initialLoadDoneRef = useRef(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('inbox') === 'true' || params.get('inbox') === '1' || params.get('openInbox') === 'true') {
      setIsOpen(true);
      refreshInboxState();
    }
  }, [location.search]);

  const getCategoryMeta = (category) => CATEGORY_META[category] || CATEGORY_META.system;

  const isTransportUser = Array.isArray(user?.roles)
    && (user.roles.includes('transportista') || user.roles.includes('admin') || user.roles.includes('tote'));

  const getVisibleTabs = (currentMessages) => {
    const preferredOrder = ['all', 'system', 'transport', 'games', 'commerce'];
    return preferredOrder.filter((key) => {
      if (key === 'all') return true;
      if (key === 'transport' && isTransportUser) return true;
      return currentMessages.some((message) => message.category === key);
    });
  };

  const getMessageType = (message) => message?.message_type || message?.metadata?.type || 'generic';

  const getTransportKindMeta = (message) => {
    const type = String(getMessageType(message) || '').toLowerCase();
    const kind = String(message?.metadata?.kind || '').toLowerCase();
    if (type.includes('ride') || kind === 'ride') {
      return { label: 'Viaje', icon: '🚗', detailTitle: 'Detalle del viaje' };
    }
    return { label: 'Delivery', icon: '📦', detailTitle: 'Detalle de la actividad' };
  };

  const getMessageTypeLabel = (message) => {
    const type = String(getMessageType(message) || '').toLowerCase();
    if (type === 'ride_request') return 'Solicitud de viaje';
    if (type === 'delivery_request') return 'Solicitud de delivery';
    if (type === 'delivery_accepted') return 'Delivery aceptado';
    if (type === 'ride_accepted') return 'Viaje aceptado';
    return String(getMessageType(message) || 'generic').replace(/_/g, ' ');
  };

  const handleAcceptWelcome = async (message) => {
    try {
      if (!message?.metadata?.event_id) return;

      const res = await fetch(`${API_URL}/api/welcome/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ event_id: message.metadata.event_id })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Error al reclamar bienvenida');
      }

      toast.success(`🎉 Bienvenida reclamada: +${data.coins_received || 0}🪙 +${data.fires_received || 0}🔥`);

      await markAsRead(message.id);
      setMessages(prev => prev.filter(m => m.id !== message.id));

      // Refrescar wallet y estadísticas relacionadas
      queryClient.invalidateQueries(['wallet-balance']);
      queryClient.invalidateQueries(['user-wallet']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (err) {
      toast.error(getBackendErrorMessage(err, 'Error al reclamar bienvenida'));
    }
  };

  // Handle settlement claim
  const handleClaimSettlement = async (message) => {
    try {
      if (message?.metadata?.type !== 'settlement_claim') return;
      if (message.metadata?.claimed) {
        toast('Ya reclamaste esta comisión');
        return;
      }

      const res = await fetch(`${API_URL}/api/messages/${message.id}/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Error al reclamar comisión');
      }

      if (data.alreadyClaimed) {
        toast('Ya habías reclamado esta comisión anteriormente');
      } else {
        toast.success(`✅ ${data.message || `Reclamado 🔥${data.amount} exitosamente`}`);
      }

      // Update message in state
      setMessages(prev => prev.map(m => 
        m.id === message.id 
          ? { ...m, metadata: { ...m.metadata, claimed: true } }
          : m
      ));

      // Refresh wallet
      queryClient.invalidateQueries(['wallet-balance']);
      queryClient.invalidateQueries(['user-wallet']);
    } catch (err) {
      toast.error(getBackendErrorMessage(err, 'Error al reclamar comisión'));
    }
  };

  const handleViewTransportRequest = async (message) => {
    const requestId = message?.metadata?.request_id;
    if (!requestId) {
      toast.error('ID de solicitud no encontrado en el mensaje');
      return;
    }

    // Cerrar buzón inmediatamente para evitar bloqueos visuales
    setIsOpen(false);

    try {
      // Marcamos como leído si aún no lo está (no esperamos para navegar)
      if (!message.is_read) {
        markAsRead(message.id).catch(err => console.error('Error marking as read:', err));
      }
      
      // Navegar a la página de transporte con el ID
      navigate(`/transport?requestId=${requestId}`);
    } catch (err) {
      console.error('Navigation error:', err);
      navigate(`/transport?requestId=${requestId}`);
    }
  };


  const refreshInboxState = async () => {
    try {
      const [listRes, unreadRes] = await Promise.all([
        fetch(`${API_URL}/api/messages`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API_URL}/api/messages/unread-count`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (listRes.ok) {
        const data = await listRes.json();
        setMessages(data.messages || []);
      }
      if (unreadRes.ok) {
        const data = await unreadRes.json();
        setUnreadCount(data.unread || 0);
      }
    } catch (err) {
      // noop
    }
  };

  useEffect(() => {
    if (user) {
      if (disablePrefetch) return;
      if (initialLoadDoneRef.current) return;
      if (typeof window !== 'undefined' && window.__MX_DISABLE_INBOX_PREFETCH__) {
        initialLoadDoneRef.current = true;
        return;
      }
      initialLoadDoneRef.current = true;
      refreshInboxState();
      // Opcional: habilitar polling si se requiere actualización periódica
      // const interval = setInterval(loadMessages, 60000);
      // return () => clearInterval(interval);
    }
  }, [user, disablePrefetch]);

  useEffect(() => {
    if (disablePrefetch) return;
    if (!socket) return;
    const handleNotification = (payload) => {
      const now = Date.now();
      const MIN_REFRESH_INTERVAL_MS = 1500;
      if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) {
        return;
      }
      lastRefreshRef.current = now;

      refreshInboxState();

      // Send push notification if app is not in focus
      if (document.hidden && user?.id) {
        axios.post('/api/push/send', {
          user_id: user.id,
          title: 'MUNDOXYZ',
          body: 'Tienes un nuevo mensaje en tu buzón',
          url: '/messages'
        }).catch(err => console.error('Push notification send error:', err));
      }
    };
    const handleTransportNewRequest = () => {
      const now = Date.now();
      const MIN_REFRESH_INTERVAL_MS = 1500;
      if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) {
        return;
      }
      lastRefreshRef.current = now;
      refreshInboxState();
    };
    socket.on('notification', handleNotification);
    socket.on('transport:new_request', handleTransportNewRequest);
    return () => {
      socket.off('notification', handleNotification);
      socket.off('transport:new_request', handleTransportNewRequest);
    };
  }, [socket, isOpen, disablePrefetch]);

  const loadMessages = async () => {
    await refreshInboxState();
  };

  const markAsRead = async (messageId) => {
    try {
      await fetch(`${API_URL}/api/messages/${messageId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
        setUnreadCount(0);
        toast.success(`${data.count || 'Todos'} mensajes marcados como leídos`);
      }
    } catch (err) {
      toast.error('Error al marcar todos como leídos');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      // No hay endpoint de borrado en /api/messages
      // Marcamos como leído y lo ocultamos del listado local
      await markAsRead(messageId);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.category === filter;
  });
  const visibleTabs = getVisibleTabs(messages);

  const handleMessageClick = async (message) => {
    try {
      // Si es resultado de rifa con código asociado, redirigir a la rifa
      const raffleCode = message?.metadata?.raffleCode;
      if (raffleCode) {
        await markAsRead(message.id);
        setIsOpen(false);
        navigate(`/raffles/${raffleCode}`);
        return;
      }

      // Comportamiento por defecto: solo marcar como leído si está sin leer
      if (!message.is_read) {
        await markAsRead(message.id);
      }
    } catch (err) {
      // Silenciar errores para no romper UX del inbox
      // console.error('Error handling message click:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (days === 1) {
      return 'Ayer';
    } else if (days < 7) {
      return `Hace ${days} días`;
    } else {
      return date.toLocaleDateString('es-ES');
    }
  };

  return (
    <>
      {/* Inbox Button */}
      <button
        className="inbox-button"
        data-tutorial-target="inbox-button"
        onClick={() => { const next = !isOpen; setIsOpen(next); if (next) loadMessages(); }}
        title="Mensajes"
      >
        📬
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount}</span>
        )}
      </button>

      {/* Inbox Modal */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div className="inbox-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="inbox-modal" onClick={e => e.stopPropagation()}>
            <div className="inbox-header">
              <div className="inbox-title-block">
                <h2>📬 Buzón</h2>
                <p>Centro estándar de mensajes y notificaciones</p>
              </div>
              <div className="inbox-header-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => window.open('https://t.me/+DXLjmoWzEJc0NDZh', '_blank')}
                  title="Abrir grupo oficial de Telegram"
                >
                  📢 Grupo
                </button>
                <button className="close-btn" onClick={() => setIsOpen(false)}>
                  ×
                </button>
              </div>
            </div>

            <div className="inbox-tabs">
              {visibleTabs.map((tabKey) => {
                const meta = getCategoryMeta(tabKey);
                const count = tabKey === 'all'
                  ? messages.length
                  : messages.filter((message) => message.category === tabKey).length;

                return (
                  <button
                    key={tabKey}
                    className={`tab ${filter === tabKey ? 'active' : ''}`}
                    onClick={() => setFilter(tabKey)}
                    title={meta.label}
                    aria-label={`${meta.label} (${count})`}
                  >
                    <span className="tab-icon">{meta.icon}</span>
                    <span className="tab-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="messages-list">
              {unreadCount > 0 && (
                <button className="btn-read-all" onClick={markAllAsRead}>
                  ✔️ Marcar todos como leídos
                </button>
              )}
              {filteredMessages.length === 0 ? (
                <div className="no-messages">
                  <p>No tienes mensajes</p>
                </div>
              ) : (
                filteredMessages.map(message => (
                  <div 
                    key={message.id} 
                    className={`message-item ${!message.is_read ? 'unread' : ''}`}
                    onClick={() => handleMessageClick(message)}
                  >
                    <div className="message-header">
                      <div className="message-header-main">
                        <span className="message-category">
                          {getCategoryMeta(message.category).icon}
                          {getCategoryMeta(message.category).label}
                        </span>
                        <span className={`message-type-pill ${message.category === 'transport' ? 'transport' : ''}`}>
                          {message.category === 'transport' ? `${getTransportKindMeta(message).icon} ${getMessageTypeLabel(message)}` : getMessageTypeLabel(message)}
                        </span>
                      </div>
                      <span className="message-date">
                        {formatDate(message.created_at)}
                      </span>
                    </div>
                    
                    <h4 className="message-title">{message.title}</h4>
                    <p className="message-content">{message.content}</p>
                    
                    {message.metadata && (
                      <div className="message-metadata">
                        {message.metadata.raffleCode && (
                          <span>Rifa: #{message.metadata.raffleCode}</span>
                        )}
                        {message.metadata.winningNumber && (
                          <span>Número ganador: {message.metadata.winningNumber}</span>
                        )}
                        {(message.metadata.prizeAmount !== undefined) && (
                          <span>Premio: {message.metadata.prizeAmount} {message.metadata.currency === 'fires' ? '🔥' : message.metadata.currency === 'coins' ? '🪙' : ''}</span>
                        )}
                        {(message.metadata.type === 'welcome_event' || message.metadata.type === 'welcome_event_notification') && (
                          <span>🎁 {message.metadata.coins_amount || 0}🪙  {message.metadata.fires_amount || 0}🔥</span>
                        )}
                      </div>
                    )}

                    {message.metadata?.type === 'welcome_event' && message.metadata.event_id && (
                      <button
                        className="btn-primary w-full mt-3"
                        data-tutorial-target="welcome-message-accept"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptWelcome(message);
                        }}
                      >
                        🎁 Aceptar Bienvenida
                      </button>
                    )}

                    {message.metadata?.type === 'welcome_event_notification' && (
                      <div className="welcome-notification-banner w-full mt-3 p-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm text-center">
                        ✅ Premio de bienvenida acreditado: +{message.metadata.coins_amount || 0}🪙  +{message.metadata.fires_amount || 0}🔥
                      </div>
                    )}

                    {message.metadata?.type === 'settlement_claim' && !message.metadata?.claimed && (
                      <button
                        className="btn-primary w-full mt-3 bg-accent/20 hover:bg-accent/30 border-accent/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClaimSettlement(message);
                        }}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span>💰 Aceptar</span>
                          <span className="font-bold">🔥 {Number(message.metadata?.amount || 0).toFixed(2)}</span>
                        </span>
                      </button>
                    )}

                    {message.metadata?.type === 'store_sale' && message.metadata?.url && (
                      <button
                        className="btn-primary w-full mt-3 bg-emerald-600/20 hover:bg-emerald-600/30 border-emerald-500/50"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!message.is_read) await markAsRead(message.id);
                          setIsOpen(false);
                          navigate(message.metadata.url);
                        }}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span>🧾 Ver pedido</span>
                        </span>
                      </button>
                    )}

                    {['delivery_request', 'ride_request'].includes(getMessageType(message)) && (
                      <button
                        className="btn-primary w-full mt-3 bg-blue-600/20 hover:bg-blue-600/30 border-blue-500/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewTransportRequest(message);
                        }}
                        disabled={isLoadingRequest}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span>{getTransportKindMeta(message).icon} Ver Actividad</span>
                        </span>
                      </button>
                    )}
                    
                    {message.metadata?.type === 'document_ready' && message.metadata.pdf_url && (
                      <button
                        className="btn-primary w-full mt-3 bg-emerald-600/20 hover:bg-emerald-600/30 border-emerald-500/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(message.metadata.pdf_url, '_blank');
                        }}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span>📄 Ver Documento</span>
                        </span>
                      </button>
                    )}
                    
                    {message.metadata?.type === 'gift_pending' && message.metadata.gift_id && (
                      <GiftClaimButton
                        giftId={message.metadata.gift_id}
                        coinsAmount={message.metadata.coins_amount || 0}
                        firesAmount={message.metadata.fires_amount || 0}
                        onClaimed={() => {
                          deleteMessage(message.id);
                          loadMessages();
                        }}
                      />
                    )}
                    
                    <button 
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMessage(message.id);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
};

export default MessageInbox;
